import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Send, MessageSquare, Mic, Square } from "lucide-react";
import ProductCards from "../agents/components/ProductCards";
import { getTheme } from "./themes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Progressive feedback shown to the visitor while the cascade pipeline
// (STT → LLM + RAG → TTS) is running. Index 0 first, advance every ~1.6s,
// clamp at the last entry until the response arrives.
const THINKING_PHASES = [
  "Listening to your question…",
  "Connecting to the knowledge base…",
  "Thinking through the answer…",
  "Composing the reply…",
];

export default function EmbedWidget() {
  const { agentId } = useParams();
  const STORAGE_KEY = `hellosir:${agentId}`;
  const restored = (() => { try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; } })();
  // When embedded (?embed=1) we always boot into the safer Chat tab — voice
  // can be opened by the visitor explicitly. Avoids stale "voice" state from
  // a previous session that triggers repeated mic prompts.
  const isEmbedded = typeof window !== "undefined" && /[?&]embed=1\b/.test(window.location.search);
  const initialTab = isEmbedded ? "chat" : (restored.tab || "chat");
  const [agent, setAgent] = useState(null);
  const theme = getTheme(agent?.theme);
  // Always start open on mount — host page (widget-embed.js) controls iframe visibility via launcher
  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState(initialTab);
  const [messages, setMessages] = useState(restored.messages || []);
  const [input, setInput] = useState("");
  const [convId, setConvId] = useState(restored.convId || null);
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef(null);
  const voiceScrollRef = useRef(null);

  // Voice (Cascade pipeline: MediaRecorder → STT → LLM → TTS) with continuous VAD
  const [voiceStatus, setVoiceStatus] = useState("idle"); // idle | listening | recording | thinking | speaking
  // Drop any legacy "Could not transcribe…" assistant bubbles that may have
  // been persisted by an older buggy build. They look like real replies but
  // are actually backend errors — purging here makes a returning visitor's
  // history look clean.
  const _cleanVoiceTurns = (turns = []) => turns.filter((t) => !(t.role === "assistant" && /could not transcribe|empty result|too short/i.test(t.content || "")));
  const [voiceTurns, setVoiceTurns] = useState(_cleanVoiceTurns(restored.voiceTurns || []));
  const [thinkingPhase, setThinkingPhase] = useState(0); // 0..3 — progressive feedback while waiting
  const conversationActiveRef = useRef(false);
  const greetedRef = useRef(restored.voiceGreeted || false); // only greet once per visitor session
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const localStreamRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const vadFrameRef = useRef(null);
  const lastVoiceAtRef = useRef(0);
  const recStartedAtRef = useRef(0);
  const autoResumeRef = useRef(!!restored.autoResumeVoice);
  const priorVoiceTurnsRef = useRef(restored.voiceTurns || []);

  // Persist EVERYTHING the user might want preserved across page navigations.
  // We also write synchronously inside the voice-turn updaters and on `pagehide`
  // so the last assistant turn isn't lost when the host page navigates mid-call.
  const persist = (state) => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* noop */ }
  };
  useEffect(() => {
    persist({ messages, convId, voiceTurns, tab, autoResumeVoice: voiceStatus !== "idle", voiceGreeted: greetedRef.current });
  }, [messages, convId, voiceTurns, tab, voiceStatus, STORAGE_KEY]);

  useEffect(() => {
    const onLeave = () => persist({ messages, convId, voiceTurns, tab, autoResumeVoice: voiceStatus !== "idle", voiceGreeted: greetedRef.current });
    window.addEventListener("pagehide", onLeave);
    window.addEventListener("beforeunload", onLeave);
    return () => { window.removeEventListener("pagehide", onLeave); window.removeEventListener("beforeunload", onLeave); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, convId, voiceTurns, tab, voiceStatus]);

  // When user closes the chat, tell the host page so it shows the launcher bubble (not an empty iframe)
  const handleClose = () => {
    setOpen(false);
    stopVoiceRef.current?.();
    try { window.parent.postMessage("hellosir:close", "*"); } catch { /* noop */ }
  };

  // The parent host page (LandingChatBubble) can ask us to terminate voice when
  // the user clicks the outer floating bubble close button while voice is live.
  const stopVoiceRef = useRef(null);
  useEffect(() => {
    const onMsg = (e) => {
      if (!e?.data) return;
      const type = typeof e.data === "string" ? e.data : e.data.type;
      if (type === "hellosir:host-close") stopVoiceRef.current?.();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    axios.get(`${API}/public/agent/${agentId}`)
      .then(({ data }) => {
        setAgent(data);
        // Only seed welcome if no prior conversation
        setMessages((prev) => prev.length > 0 ? prev : [{ role: "assistant", content: data.welcome_message }]);
      })
      .catch(() => setAgent({ unavailable: true }));
  }, [agentId]);

  // After the agent loads, tell the host page to keep the widget open (it may have collapsed
  // briefly during navigation). Voice context is preserved server-side via conversation_id,
  // so the user simply taps the mic on the new page to keep talking — no auto-mic needed.
  useEffect(() => {
    if (!agent || agent.unavailable) return;
    try { window.parent.postMessage("hellosir:open", "*"); } catch { /* noop */ }
    // Reset the (now legacy) auto-resume flag so it doesn't get stuck.
    autoResumeRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, thinking]);
  useEffect(() => { voiceScrollRef.current?.scrollTo({ top: voiceScrollRef.current.scrollHeight, behavior: "smooth" }); }, [voiceTurns, voiceStatus]);

  // While the cascade pipeline (STT → LLM → TTS) is running we keep the user
  // visually engaged with rotating progress phrases. The pipeline can take
  // 7–10s end-to-end, so silence here feels broken. Reset to phase 0 on every
  // entry into the "thinking" state, advance every ~1.6s, and stop cycling
  // once we leave thinking (the timer is cleared on cleanup).
  useEffect(() => {
    if (voiceStatus !== "thinking") return;
    setThinkingPhase(0);
    const id = setInterval(() => setThinkingPhase((p) => Math.min(p + 1, THINKING_PHASES.length - 1)), 1600);
    return () => clearInterval(id);
  }, [voiceStatus]);

  // Auto-stop voice on tab change / window hidden
  useEffect(() => {
    if (tab !== "voice" && voiceStatus !== "idle") stopVoice();
    const onVis = () => { if (document.hidden && voiceStatus !== "idle") stopVoice(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, voiceStatus]);

  // Auto-greet: the moment the user opens the Voice tab (for the first time this
  // session), play the agent's welcome message and then start continuous VAD.
  // Same behaviour everywhere — dashboard voice tester and embed bubble — so
  // the visitor never has to keep tapping the mic between turns.
  useEffect(() => {
    if (tab !== "voice" || !agent || agent.unavailable || greetedRef.current || voiceStatus !== "idle") return;
    greetedRef.current = true;
    autoGreet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, agent]);

  const autoGreet = async () => {
    conversationActiveRef.current = true;
    setVoiceStatus("thinking");
    let data;
    try {
      ({ data } = await axios.post(`${API}/voice/greet-public`, { agent_id: agentId }));
    } catch {
      // Greet failed → still open the mic so user can speak
      _openMicSilently();
      setVoiceStatus("listening");
      return;
    }
    setVoiceTurns((t) => [...t, { role: "assistant", content: data.text }]);
    try {
      const audio = new Audio(`data:${data.audio_mime || "audio/mpeg"};base64,${data.audio_base64}`);
      audioPlayerRef.current = audio;
      setVoiceStatus("speaking");
      // CRITICAL: do NOT open the mic while the greeting is playing. Speaker
      // bleed-through gets transcribed by Whisper and produces garbage like
      // "Subs by www.zeoranger.co.uk" + "Could not transcribe audio". The mic
      // only opens AFTER the greeting finishes so the very first thing
      // Whisper sees is the user's real voice.
      const openMicAndListen = () => {
        if (!conversationActiveRef.current) return;
        _openMicSilently();
        setVoiceStatus("listening");
      };
      audio.onended = openMicAndListen;
      audio.onerror = openMicAndListen;
      await audio.play();
    } catch {
      // Browser blocked autoplay — just open mic and wait for user.
      _openMicSilently();
      setVoiceStatus("listening");
    }
  };

  // Opens the mic + audio context + analyser and starts the VAD loop without
  // changing voiceStatus. Used during auto-greet so barge-in works mid-greeting.
  const _openMicSilently = async () => {
    if (localStreamRef.current && analyserRef.current) {
      runVadLoop();
      return;
    }
    try {
      const ms = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      localStreamRef.current = ms;
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(ms);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      runVadLoop();
    } catch {
      // Mic permission denied — barge-in won't work but greet still plays
    }
  };

  const send = async () => {
    if (!input.trim()) return;
    const msg = input.trim();
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput(""); setThinking(true);
    try {
      const { data } = await axios.post(`${API}/chat/widget`, {
        agent_id: agentId, message: msg, conversation_id: convId, visitor_id: "demo-visitor",
      });
      setConvId(data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, products: data.products }]);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let reply = "Sorry, something went wrong. Please try again.";
      if (status === 402) {
        reply = detail || "This agent is out of chat credits — the site owner needs to top up.";
      } else if (detail) {
        reply = detail;
      }
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } finally { setThinking(false); }
  };

  // VAD tuning constants
  const SPEECH_THRESHOLD = 18;      // freq-bin avg above which we count as speech
  const BARGE_IN_THRESHOLD = 32;    // higher bar while agent is speaking (resists echo leak)
  const SILENCE_HOLD_MS = 1300;     // user must be silent this long to finish a turn
  const MIN_UTTERANCE_MS = 400;     // shorter blips are discarded (mic pops)
  const MAX_UTTERANCE_MS = 30000;   // hard cap so a long monologue still gets sent

  // Keep the latest voiceStatus available inside the VAD requestAnimationFrame loop
  // without re-creating it every state change (closures would be stale).
  const voiceStatusRef = useRef("idle");
  useEffect(() => { voiceStatusRef.current = voiceStatus; }, [voiceStatus]);

  const teardownAudio = () => {
    try { vadFrameRef.current && cancelAnimationFrame(vadFrameRef.current); } catch { /* noop */ }
    vadFrameRef.current = null;
    try { mediaRecorderRef.current?.state !== "inactive" && mediaRecorderRef.current?.stop(); } catch { /* noop */ }
    mediaRecorderRef.current = null;
    try { localStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    localStreamRef.current = null;
    try { audioCtxRef.current?.close(); } catch { /* noop */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
    try { audioPlayerRef.current?.pause(); } catch { /* noop */ }
    audioPlayerRef.current = null;
  };

  const startVoice = async () => {
    // Begin a continuous conversation: open the mic ONCE, then VAD auto-detects
    // when the user speaks and when they finish.
    if (voiceStatus !== "idle" && voiceStatus !== "speaking") return;
    return _openMicAndListen();
  };

  const _openMicAndListen = async () => {
    conversationActiveRef.current = true;
    // If mic already open (e.g. resuming after a turn), just (re)start the VAD loop
    if (localStreamRef.current && analyserRef.current) {
      setVoiceStatus("listening");
      runVadLoop();
      return;
    }
    setVoiceStatus("listening");
    try {
      const ms = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      localStreamRef.current = ms;
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(ms);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      runVadLoop();
    } catch (err) {
      conversationActiveRef.current = false;
      setVoiceStatus("idle");
    }
  };

  const stopVoice = () => {
    conversationActiveRef.current = false;
    teardownAudio();
    setVoiceStatus("idle");
  };
  useEffect(() => { stopVoiceRef.current = stopVoice; });

  // -------- 60-second inactivity auto-shutdown --------
  // If voice is live and the visitor doesn't speak for 60s, gracefully stop
  // voice AND close the widget — saves credits and avoids a hanging mic.
  const INACTIVITY_MS = 60_000;
  const inactivityTimerRef = useRef(null);
  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      if (!conversationActiveRef.current) return;
      stopVoice();
      // Tell the host page to collapse the floating bubble too
      try { window.parent.postMessage("hellosir:idle-timeout", "*"); } catch { /* noop */ }
      setOpen(false);
    }, INACTIVITY_MS);
  };
  useEffect(() => {
    // Only run the timer while voice is actively running
    if (voiceStatus === "idle") {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      return;
    }
    // Any non-idle state (listening / recording / thinking / speaking) resets the clock
    resetInactivityTimer();
    return () => { if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceStatus, voiceTurns.length]);

  const beginUtterance = () => {
    const ms = localStreamRef.current;
    if (!ms) return;
    const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
    const rec = new MediaRecorder(ms, { mimeType: mime });
    mediaRecorderRef.current = rec;
    audioChunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(audioChunksRef.current, { type: mime });
      audioChunksRef.current = [];
      const duration = Date.now() - recStartedAtRef.current;
      if (duration < MIN_UTTERANCE_MS || blob.size < 1500) {
        // Too short → discard and resume listening
        if (conversationActiveRef.current) {
          setVoiceStatus("listening");
          // VAD loop keeps running; no need to restart
        }
        return;
      }
      sendVoiceTurn(blob, mime);
    };
    recStartedAtRef.current = Date.now();
    rec.start();
    setVoiceStatus("recording");
  };

  const bargeIn = () => {
    // User started speaking while the agent was talking. Stop playback immediately
    // and start capturing the new utterance.
    try { audioPlayerRef.current?.pause(); audioPlayerRef.current = null; } catch { /* noop */ }
    beginUtterance();
  };

  const runVadLoop = () => {
    // Cancel any previous frame before starting a fresh loop
    if (vadFrameRef.current) {
      try { cancelAnimationFrame(vadFrameRef.current); } catch { /* noop */ }
      vadFrameRef.current = null;
    }
    const analyser = analyserRef.current;
    if (!analyser || !conversationActiveRef.current) return;
    const data = new Uint8Array(analyser.frequencyBinCount);
    lastVoiceAtRef.current = 0;
    const tick = () => {
      if (!conversationActiveRef.current || !analyserRef.current) return;
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      const avg = sum / data.length;
      const now = Date.now();
      const status = voiceStatusRef.current;

      if (status === "listening") {
        if (avg > SPEECH_THRESHOLD) {
          beginUtterance();
          lastVoiceAtRef.current = now;
        }
      } else if (status === "recording") {
        if (avg > SPEECH_THRESHOLD) lastVoiceAtRef.current = now;
        const sinceVoice = now - lastVoiceAtRef.current;
        const elapsed = now - recStartedAtRef.current;
        if (sinceVoice > SILENCE_HOLD_MS || elapsed > MAX_UTTERANCE_MS) {
          try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
        }
      } else if (status === "speaking") {
        // Barge-in: user starts talking while agent is speaking → interrupt
        if (avg > BARGE_IN_THRESHOLD) {
          bargeIn();
          lastVoiceAtRef.current = now;
        }
      }
      // status === "thinking" → just keep the loop alive

      vadFrameRef.current = requestAnimationFrame(tick);
    };
    vadFrameRef.current = requestAnimationFrame(tick);
  };

  const sendVoiceTurn = async (blob, mime) => {
    setVoiceStatus("thinking");
    const fd = new FormData();
    const ext = mime.includes("webm") ? "webm" : "mp4";
    fd.append("audio", blob, `voice.${ext}`);
    fd.append("agent_id", agentId);
    if (convId) fd.append("conversation_id", convId);
    fd.append("visitor_id", "widget");
    let data;
    try {
      ({ data } = await axios.post(`${API}/voice/cascade-turn-public`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      // Empty/garbage transcription (Whisper got <0.1s of audio or pure noise
      // bleeding from the agent's own playback). DO NOT show as an assistant
      // bubble — it pollutes the chat history and looks broken. Just resume
      // listening silently.
      if (status === 400 && /transcribe|empty|too short/i.test(detail)) {
        if (conversationActiveRef.current) setVoiceStatus("listening");
        else setVoiceStatus("idle");
        return;
      }
      let msg = "Sorry, I couldn't hear you. Try again.";
      let fatal = false;
      if (status === 402) {
        msg = detail || "This agent is out of voice credits — the site owner needs to top up.";
        fatal = true; // stop the loop instead of spamming the same error
      } else if (detail) {
        msg = detail;
      }
      setVoiceTurns((t) => [...t, { role: "assistant", content: msg }]);
      if (fatal) {
        // No credits / fatal error → end the session cleanly. Auto-collapse
        // the floating bubble too so the visitor isn't stuck in a dead loop.
        conversationActiveRef.current = false;
        teardownAudio();
        setVoiceStatus("idle");
        try { window.parent.postMessage("hellosir:idle-timeout", "*"); } catch { /* noop */ }
      } else if (conversationActiveRef.current) {
        setVoiceStatus("listening");
      } else {
        setVoiceStatus("idle");
      }
      return;
    }
    setConvId(data.conversation_id);
    setVoiceTurns((t) => [
      ...t,
      { role: "user", content: data.user_text },
      { role: "assistant", content: data.assistant_text, products: data.products },
    ]);

    // If the assistant included a URL, ask the host page to navigate.
    const urlMatch = (data.assistant_text || "").match(/https?:\/\/[^\s)]+/);
    if (urlMatch) {
      try { window.top.postMessage({ type: "hellosir:navigate", url: urlMatch[0] }, "*"); } catch { /* noop */ }
    }

    // Play TTS audio. VAD loop stays alive so user can barge in.
    try {
      const audio = new Audio(`data:${data.audio_mime || "audio/mpeg"};base64,${data.audio_base64}`);
      audioPlayerRef.current = audio;
      setVoiceStatus("speaking");
      const resume = () => {
        if (conversationActiveRef.current) setVoiceStatus("listening");
        else setVoiceStatus("idle");
      };
      audio.onended = resume;
      audio.onerror = resume;
      await audio.play();
    } catch {
      if (conversationActiveRef.current) setVoiceStatus("listening");
      else setVoiceStatus("idle");
    }
  };

  if (!agent) return <div className="min-h-screen flex items-center justify-center text-sm text-[#4B5563]">Loading…</div>;
  if (agent.unavailable) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center text-sm text-[#4B5563] gap-2">
      <div className="font-display font-bold text-base text-[#0F1115]">This widget is unavailable.</div>
      <div>The agent may have been removed, paused or its status is not set to active.</div>
      <div className="text-xs font-mono mt-2">Site owner: open <span className="text-[#0044FF]">/admin/settings</span> to pick a different agent.</div>
    </div>
  );

  return (
    <div
      data-testid="embed-widget-page"
      className={
        isEmbedded
          // Embedded: fill the iframe edge-to-edge with no wrapping chrome — the
          // host page (LandingChatBubble) already gives us the panel + shadow.
          ? "h-screen w-screen bg-white flex flex-col"
          // Standalone: original floating-bottom-right card layout
          : "min-h-screen bg-[#F9FAFB] flex items-end justify-end p-6"
      }
    >
      {/* Hide Emergent preview badge inside the widget iframe */}
      <style>{`#emergent-badge, .emergent-badge, [data-emergent-badge], a[href*="emergent.sh"], a[href*="emergent.ai"] { display: none !important; visibility: hidden !important; opacity: 0 !important; pointer-events: none !important; }`}</style>
      {open ? (
        <div
          className={
            isEmbedded
              ? "w-full h-full bg-white flex flex-col"
              : "w-[380px] h-[560px] bg-white border border-[#E2E8F0] flex flex-col shadow-2xl"
          }
          data-testid="widget-window"
        >
          <div className="p-3 flex items-center justify-between" style={{ background: theme.primary, color: theme.ink }}>
            <div>
              <div className="font-display font-extrabold text-sm">{agent.name}</div>
              <div className="text-[10px] font-mono opacity-70">powered by HelloSir.ai</div>
            </div>
            {!isEmbedded && (
              <button onClick={handleClose} data-testid="widget-close-button" className="opacity-70 hover:opacity-100 text-sm">×</button>
            )}
          </div>

          {/* Tab toggle — chat vs voice */}
          <div className="flex border-b border-[#E2E8F0]">
            <button
              onClick={() => setTab("chat")}
              className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 ${tab === "chat" ? "text-[#030712] border-b-2" : "text-[#4B5563] border-b-2 border-transparent"}`}
              style={tab === "chat" ? { borderBottomColor: theme.primary } : undefined}
              data-testid="widget-tab-chat"
            ><MessageSquare className="w-3.5 h-3.5" /> Chat</button>
            <button
              onClick={() => setTab("voice")}
              className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 ${tab === "voice" ? "text-[#030712] border-b-2" : "text-[#4B5563] border-b-2 border-transparent"}`}
              style={tab === "voice" ? { borderBottomColor: theme.primary } : undefined}
              data-testid="widget-tab-voice"
            ><Mic className="w-3.5 h-3.5" /> Voice</button>
          </div>

          {tab === "chat" && (
            <>
              <div ref={scrollRef} className="flex-1 overflow-auto p-4 space-y-2 text-sm" data-testid="widget-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className="max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap"
                      style={m.role === "user" ? { background: theme.primary, color: theme.ink } : { background: "#F3F4F6" }}
                    >
                      {m.content}
                      {m.products?.length > 0 && <ProductCards products={m.products} compact />}
                    </div>
                  </div>
                ))}
                {thinking && <div className="text-xs text-[#4B5563]">…</div>}
              </div>
              <div className="border-t border-[#E2E8F0] p-3 flex gap-2">
                <input data-testid="widget-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type your message…" className="flex-1 border border-[#E2E8F0] px-3 py-2 rounded-sm text-sm" />
                <button data-testid="widget-send-button" onClick={send} disabled={thinking || !input.trim()} className="text-sm px-3 py-2 rounded-sm font-medium disabled:opacity-50" style={{ background: theme.primary, color: theme.ink }}><Send className="w-4 h-4" /></button>
              </div>
            </>
          )}

          {tab === "voice" && (
            <div className="flex-1 flex flex-col bg-gradient-to-b from-[#030712] to-[#0B1220] text-white">
              <div ref={voiceScrollRef} className="flex-1 overflow-auto p-4 space-y-2" data-testid="widget-voice-timeline">
                {voiceTurns.length === 0 && voiceStatus !== "thinking" && (
                  <div className="text-center text-white/50 text-xs mt-12">
                    <div className="font-mono text-[10px] uppercase tracking-widest">
                      {voiceStatus === "listening" ? "listening…" :
                       voiceStatus === "recording" ? "you're speaking…" :
                       voiceStatus === "speaking" ? "speaking…" :
                       "tap the mic to start a conversation"}
                    </div>
                  </div>
                )}
                {voiceTurns.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className="max-w-[88%] px-3 py-2 rounded-sm text-sm"
                      style={m.role === "user" ? { background: theme.primary, color: theme.ink } : { background: "rgba(255,255,255,0.1)" }}
                    >
                      <div className="text-[9px] font-mono uppercase opacity-60 mb-1">{m.role === "user" ? "YOU" : "ASSISTANT"}</div>
                      {m.content && <div className="whitespace-pre-wrap">{m.content}</div>}
                      {m.products?.length > 0 && <div className="-mx-1 mt-2"><ProductCards products={m.products} compact /></div>}
                    </div>
                  </div>
                ))}
                {voiceStatus === "thinking" && (
                  <div className="flex justify-start" data-testid="voice-thinking-bubble">
                    <div className="max-w-[88%] px-3 py-2.5 rounded-sm text-sm bg-white/8 border border-white/10">
                      <div className="text-[9px] font-mono uppercase opacity-60 mb-1.5">ASSISTANT</div>
                      <div className="flex items-center gap-2 text-white/85">
                        <span className="flex gap-1" aria-hidden>
                          <span className="w-1.5 h-1.5 rounded-full bg-[#FBBF24] animate-bounce" style={{ animationDelay: "0ms" }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] animate-bounce" style={{ animationDelay: "120ms" }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C] animate-bounce" style={{ animationDelay: "240ms" }} />
                        </span>
                        <span className="text-[12px] leading-tight">{THINKING_PHASES[thinkingPhase]}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 flex flex-col items-center justify-center gap-2">
                {voiceStatus === "idle" ? (
                  <>
                    <button data-testid="widget-voice-start" onClick={startVoice} className="w-14 h-14 rounded-full text-white flex items-center justify-center shadow-lg transition-colors" style={{ background: theme.primary }}><Mic className="w-6 h-6" /></button>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">tap to start</div>
                  </>
                ) : (
                  <>
                    <button data-testid="widget-voice-stop" onClick={stopVoice} className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all ${
                      voiceStatus === "recording" ? "bg-[#EF4444] shadow-[#EF4444]/40 animate-pulse" :
                      voiceStatus === "listening" ? "bg-[#10B981] shadow-[#10B981]/40" :
                      "bg-white/10"
                    } text-white`}>
                      {voiceStatus === "thinking" || voiceStatus === "speaking" ? (
                        <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-white/60 text-center">
                      {voiceStatus === "listening" ? "listening… (tap to end)" :
                       voiceStatus === "recording" ? "speak now…" :
                       voiceStatus === "thinking" ? THINKING_PHASES[thinkingPhase] :
                       "agent speaking…"}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <button onClick={() => setOpen(true)} data-testid="widget-launcher" className="w-14 h-14 text-white rounded-full flex items-center justify-center shadow-xl" style={{ background: theme.primary }}>
          <MessageSquare className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
